import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { SftpImportConfigurationWorkflow, ImportHierarchyOption, InventoryColumnMapping } from '../../../store/Models/Api/SftpImportModels';
import { ColumnMappingCard } from '../components/ColumnMappingCard';
import { ColumnMappingHelper } from '../helpers/ColumnMappingHelper';

interface ColumnMappingDisplaySectionLocalProps {
    configData: SftpImportConfigurationWorkflow;
    onConfigChange?: (data: Partial<SftpImportConfigurationWorkflow>) => void;
    isReadOnly?: boolean;
    showAddButton?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

type ColumnMappingDisplaySectionProps = ColumnMappingDisplaySectionLocalProps & ToxnotComponentProperties;

interface ColumnMappingDisplaySectionState {
    extensibleColumns: string[];
    isLoadingExtensibleColumns: boolean;
}

class ColumnMappingDisplaySectionComponent extends ToxnotComponent<ColumnMappingDisplaySectionProps, ColumnMappingDisplaySectionState> {
    
    constructor(props: ColumnMappingDisplaySectionProps) {
        super(props);
        this.state = {
            extensibleColumns: [],
            isLoadingExtensibleColumns: true
        };
    }

    async componentDidMount() {
        // Fetch extensible columns from BOM template
        try {
            const extensibleColumns = await ColumnMappingHelper.getExtensibleColumns();
            this.setState({ 
                extensibleColumns, 
                isLoadingExtensibleColumns: false 
            });
        } catch (error) {
            this.setState({ 
                extensibleColumns: [], 
                isLoadingExtensibleColumns: false 
            });
        }
    }

    // Generate mapping options based on import type
    private getMappingOptions = () => {
        const { configData, appServices } = this.props;
        const { extensibleColumns } = this.state;
        return ColumnMappingHelper.getMappingOptions(configData, appServices, extensibleColumns);
    };

    // Handle mapping changes for editable mode
    private handleMappingChange = (index: number, selectedValue: string) => {
        const { configData, onConfigChange } = this.props;
        if (!onConfigChange) return;

        const newMappings = [...configData.spreadsheetHeadersToColumnIdMappings];
        newMappings[index] = { ...newMappings[index], columnId: selectedValue };
        
        // Apply validation logic for editable mode
        const updatedMappings = ColumnMappingHelper.applyDuplicateColumnLevelValidation(
            newMappings, 
            index
        );
        
        onConfigChange({ spreadsheetHeadersToColumnIdMappings: updatedMappings });
    };

    private handleLevelChange = (index: number, level: number | null) => {
        const { configData, onConfigChange } = this.props;
        if (!onConfigChange) return;

        const newMappings = [...configData.spreadsheetHeadersToColumnIdMappings];
        newMappings[index] = { ...newMappings[index], level };
        
        // Apply validation logic for editable mode
        const updatedMappings = ColumnMappingHelper.applyDuplicateColumnLevelValidation(
            newMappings, 
            index
        );
        
        onConfigChange({ spreadsheetHeadersToColumnIdMappings: updatedMappings });
    };

    // Helper function to determine if a mapping card should be inactive
    private isCardInactive = (mapping: InventoryColumnMapping) => {
        return ColumnMappingHelper.isCardInactive(mapping, this.props.configData.importHierarchyOptionType || ImportHierarchyOption.LevelColumn);
    };

    public render() {
        const { configData, isReadOnly = false, showAddButton = false, className = "", style } = this.props;
        const { isLoadingExtensibleColumns } = this.state;
        
        // Show loading state while fetching extensible columns
        if (isLoadingExtensibleColumns) {
            return (
                <div className={className} style={style}>
                    <div className="loading-extensible-columns">
                        Loading column options...
                    </div>
                </div>
            );
        }
        
        const mappingOptions = this.getMappingOptions();

        return (
            <div className={className} style={style}>

                <div className="column-mappings-container">
                    {
                        configData.spreadsheetHeadersToColumnIdMappings.map((mapping, index) => (
                            <div className="tn-section-col-3" key={`${mapping.headerName}-${index}`} style={{ marginBottom: '10px' }}>
                                <ColumnMappingCard
                                    columnName={mapping.headerName}
                                    selectedValue={mapping.columnId}
                                    isReadOnly={isReadOnly}
                                    onChange={isReadOnly ? () => {} : (_, selectedValue) => this.handleMappingChange(index, selectedValue)}
                                    mappingOptions={mappingOptions}
                                    level={mapping.level}
                                    onLevelChange={isReadOnly ? () => {} : (_, level) => this.handleLevelChange(index, level)}
                                    hierarchyOption={configData.importHierarchyOptionType}
                                    isInactive={this.isCardInactive(mapping)}
                                />
                            </div>
                        )
                    )}
                </div>
            </div>
        );
    }
}

export const ColumnMappingDisplaySection = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(ColumnMappingDisplaySectionComponent);
